/**
 * The brief: everything a technician is allowed to know when they speak.
 *
 * Assembled from the scene at the moment of each line - who this person is,
 * the tool they are at, what its screen says right now, the open work order
 * - and handed to the model as the ONLY source of facts. See TALK.md for the
 * rule this serves. Nothing here is computed; every field is read off
 * something the student could walk up and see for themselves.
 */

import type { CastMember } from '../fab/cast'
import { CAST, castById, castName } from '../fab/cast'
import { displayedReadout, type HmiReadout } from '../fab/hmi'
import { andonNumbers, MAINTENANCE_TOOL, type AndonState } from '../fab/bays'
import { BALLROOM } from '../fab/hall'
import { bayAt, bayNumber, getTool, TOOLS, toolBlurb, toolName, toolProcess, type Tool } from '../fab/layout'
import { currentLanguage, t, tOr } from '../i18n/i18n'
import { demosFor } from './demos'
import { HOUSE_FAULTS, HOUSE_SYSTEMS } from '../sim/house/model'
import { measured } from './predict'
import type { ExperimentReport } from '../assistant/types'
import { experimentsFor } from './experiments'
import { missionState } from '../learn/missionState'
import { talkLog, type Taught, type TalkOutcome } from './talkLog'
import { workAt, workRound, type WorkEvent, type WorkTally } from './work'
import type { Journal } from '../learn/Journal'

/** The floor as a status board reads it: every tool's tag under its state. */
export interface FloorStatus {
  running: string[]
  idle: string[]
  alarm: string[]
  down: string[]
}

/**
 * The shift at a glance, added up off the andon boards.
 *
 * Every board on the aisle carries three numbers besides its colour - lots
 * waiting, moves this shift, up-time - and the supervisor walks past all of
 * them. Totalling them is what lets him answer the question a manager
 * actually gets asked: how are we doing, and where is it stuck. Read from
 * `andonNumbers`, the same function that paints the boards, so a total
 * cannot drift from the tiles a student can walk up and add themselves.
 */
export interface ShiftSummary {
  /** Lots waiting across the floor. */
  lotsWaiting: number
  /** Moves this shift, all tools. */
  moves: number
  /** Mean up-time across the tools, per cent. */
  uptime: number
  /** Where the queue is: the worst few, most waiting first. */
  queues: Array<{ tag: string; bay: string; waiting: number }>
}

export interface TalkBrief {
  /** `persona` is style direction for the model, never a source of facts (cast.ts). */
  person: {
    name: string
    role: string
    post: string
    persona: string
    /**
     * For someone with no tool of their own: where on the aisle they are
     * standing this second, and which way they were walking. Empty for a
     * technician at a tool, whose bay is their answer.
     */
    where: string
  }
  /** `blurb` is the catalogue's one line on what the tool is for - the technician's own job description. */
  tool: { tag: string; name: string; process: string; bay: string; blurb: string } | null
  screen: { state: string; headline: string; caption: string; rows: Array<[string, string]> } | null
  workOrder: { title: string; objectives: Array<{ text: string; met: boolean }> } | null
  /**
   * THE BUILDING, when a house event is armed: which system, what it is doing
   * to the floor, and what facilities would do about it. For the chase crew,
   * who own the systems, and the supervisor, who reads their alarms. A tool
   * technician does not get this - the whole lesson is that the fault is not
   * in front of them, and a brief that told them would tell the student.
   */
  building: { system: string; fault: string; action: string } | null
  /**
   * THE LINE, for the supervisor only, while the cooling water is warm: what
   * the trip has done to etch across the building and whether the student
   * has decided what the line does about it. State, not numbers - the numbers
   * are on the Line panel, which he can put up (demo 'line-chiller').
   */
  line: { chambersDown: string | null; decided: string[] } | null
  floor: {
    down: string
    layout: string
    /** Every tool, its process and its bay, in aisle order. Where, never what. */
    directory: Array<{ tag: string; process: string; bay: string }>
    /**
     * Every tool sorted by the state its andon board is showing. Only for
     * a supervisor (cast.ts `scope`), and null for everyone else - a
     * technician speaks for their own machine and no other. Tags only, no
     * numbers: a board shows a colour, and a tool's readings belong to
     * whoever is standing in front of it.
     */
    status: FloorStatus | null
    /**
     * The shift totalled up off the same boards. Supervisor only, like
     * `status`: a technician reads their own board, not the aisle's.
     */
    shift: ShiftSummary | null
    /**
     * Who else is on shift and where they are standing.
     *
     * Everyone gets this, because everyone on a shift knows their
     * colleagues by name and which tool each one runs. Without it the
     * best anyone could manage was "ask the technician there", which
     * sends a student to look for a stranger; with it they can be sent to
     * Emily at CMP-11 in bay 07. Names and posts only - what any of those
     * people's tools are doing is still theirs to say.
     */
    roster: Array<{ name: string; role: string; where: string }>
    /**
     * THE ROUND: where this student has already been, and what they asked
     * there. Supervisor only (`scope: 'floor'`), like `status` and `shift`,
     * and for the same reason - a technician is at their tool and did not
     * see the student anywhere else; the man who walks the aisle did.
     *
     * Questions only, never answers. See talkLog.ts: the student's own
     * words carry no claim about any tool, so they can be handed on;
     * another technician's reply is a reading off a machine this man has
     * not been at, and repeating it would break the one rule the whole
     * brief exists to keep.
     *
     * `ago` is minutes, rounded, because "you were at CMP-11 ten minutes
     * ago" is how a supervisor says it and a timestamp is not.
     */
    round: Array<{ name: string; tag: string; asked: string; outcome: TalkOutcome; ago: number }> | null
    /**
     * Which tools the student has actually been INTO, and how it went there
     * - runs, out of spec, readings, calls. Supervisor only, and COUNTS
     * only: he sees people come out of a bay and he reads the boards, and
     * a rate in nm/min is not on a board. See work.ts.
     */
    worked: WorkTally[] | null
  }
  /**
   * What the student has done AT THIS TECHNICIAN'S TOOL, most recent last -
   * the knob they moved, the wafer they ran, what it came out at.
   *
   * The strongest case in the brief for anyone knowing anything: this
   * person is standing at that HMI and watched them do it. Null for the
   * supervisor, who was not in the bay - he gets `floor.worked` instead.
   *
   * Read off the journal (work.ts), which is the same record the work order
   * is evaluated against and the same one an instructor reads, so what a
   * technician says happened cannot drift from what the student hands in.
   */
  work: WorkEvent[] | null
  /**
   * WHAT THIS PERSON HAS ALREADY BEEN OVER WITH THIS STUDENT, before the
   * conversation happening now - their own questions and their own replies.
   *
   * Everyone gets this, technician and supervisor alike, because it is the
   * one thing in the brief nobody has to be careful about: they said it
   * themselves, out loud, to this student, standing where they are standing
   * now. Nothing here is another technician's word, and nothing here is a
   * reading off a tool they have not been at.
   *
   * It is what makes a second explanation different from the first, which is
   * most of what teaching is. Without it `close()` wiped the transcript and
   * Sarah met the student new every time they walked back into bay 01, and
   * explained selectivity in the same words she had used four minutes
   * earlier. See talkLog.ts for why the round the supervisor reads is a
   * narrower type than this.
   *
   * Null when they have never met.
   */
  taught: Taught | null
  /**
   * The scenes this person can put on the screen while they talk: an id, a
   * title, and the one line that says what it shows.
   *
   * The model chooses BY NAME from this list and never writes one, and
   * `takeDemo` resolves whatever name comes back against the same list
   * before anything mounts - so a scene nobody offered is not a scene. See
   * demos.ts for why that is the whole of the safety argument.
   */
  demos: Array<{ id: string; title: string; about: string }>
  /**
   * WHAT HER OWN TOOL REPORTED in this conversation, if she has run anything.
   *
   * The most surprising hole in the whole brief, and it went unnoticed for
   * three sessions of building on top of it: the transcript sent to the model
   * carries `{ role, text }` and a report turn's `text` is
   * `report.understood` - the QUESTION the assistant understood, never the
   * findings. So a technician could run an experiment on her own tool, watch
   * the report appear between them, and have no idea what it said. She only
   * ever knew a result when a prediction happened to journal one beside it.
   *
   * `findings` are the intent's own sentences, written from the numbers the
   * runs returned, taken whole and budgeted by the same rule the record uses
   * (`measured` in predict.ts). Quoting them is quoting the tool.
   *
   * Null until she runs something, and only ever HER tool in THIS
   * conversation: a report is the strongest fact in the brief and the last
   * thing that should outlive the screen it appeared on.
   */
  reported: { question: string; findings: string } | null
  /**
   * The scene RUNNING ON THE SCREEN BETWEEN THEM right now, if she put one
   * up in this conversation.
   *
   * `demos` above is the menu - what she COULD show. Nothing anywhere said
   * what she actually did show: the card mounts in the panel, `liveDemo`
   * tracks it in React state, and her next brief came back byte-identical
   * to the one before it. So she could put a simulation on the screen,
   * describe it, and then offer to show it again, because as far as the
   * brief was concerned she never had.
   *
   * `watch` is the catalogue's own line saying what to LOOK at in it -
   * authored in the repo, the same string `spokenDemoLines` reads aloud when
   * the scene goes up. It is what makes asking them what they saw possible
   * without inventing anything: she knows what the scene shows because the
   * repo wrote it down, not because a model recalled it.
   *
   * Null when the screen between them is empty.
   */
  onScreen: { id: string; title: string; watch: string; ago: number } | null
  /**
   * The experiments this person can run ON THEIR OWN TOOL, right now.
   *
   * `menu` is the tool's intent catalogue (experiments.ts) - what the
   * assistant beside that machine can actually execute. `canRun` is whether
   * this technician may reach for it at this moment, and `blocked` says why
   * not, because a refusal a student can understand is worth more than an
   * empty list: the furnace is tagged out, so Tom shows the science and runs
   * nothing until the PM is signed off.
   */
  experiments: {
    canRun: boolean
    blocked: string
    menu: Array<{ id: string; example: string; summary: string }>
  }
  language: string
}

/** What the scene needs to supply: a tool's readout, and where a person is. */
export interface BriefSources {
  readoutFor(tool: Tool): HmiReadout
  /** Where this person is standing, and which way along the aisle they walk. */
  whereIs(id: string): { x: number; z: number; dir: number } | null
  /**
   * The record of what the student has done - the one the work order is
   * evaluated against and an instructor reads. Injected rather than
   * imported so this file stays a pure function of the scene, like the
   * readout above; a harness hands it whatever journal it wants to test.
   */
  journal: Journal
  /** The armed house event's id, or null when the building is healthy. */
  houseArmed(): string | null
  /**
   * The report this technician's tool produced in the conversation on
   * screen, if any. Injected for the same reason as the journal: it lives in
   * the conversation store, and a brief that imported the store to read it
   * would stop being a pure function of what it is handed.
   */
  lastReport(): ExperimentReport | null
}

/**
 * The floor directory: every tool, its process and its bay, in aisle order.
 *
 * Where a tool IS is public knowledge - it is on the hanging bay signs, on
 * the asset tag and in the finder - and a technician who cannot point a
 * lost student at CMP-11 is not a technician. Kevin in particular exists to
 * answer this and was saying "I don't have that on my screen", because the
 * brief carried a sentence about the hall's shape and no directory.
 *
 * Positions and tags only. Nothing here says what any of those tools is
 * DOING: a reading still belongs to the person standing at it.
 */
/**
 * Where the person walking the aisle is, right now, in the terms a
 * technician would use: the bay they are level with, what is in it, and
 * which end they were heading for.
 *
 * They stop when a student engages them, so this is a still frame of a
 * moving person - which is exactly what the student is looking at.
 */
function whereOnAisle(x: number, dir: number): string {
  const bay = bayAt(x)
  if (!bay.length) return ''
  const toward = dir > 0 ? t('talk.floor.TowardGas') : dir < 0 ? t('talk.floor.TowardGowning') : ''
  return t('talk.floor.Walking', {
    bay: bayNumber(bay[0]),
    tags: bay.map((s) => s.assetTag).join(' / '),
    toward,
  })
}

/**
 * What the shift supervisor sees walking the aisle: every andon board,
 * and therefore every tool's state. The same `displayedReadout` the
 * screens are painted from, so what he says a tool is doing is what its
 * board says - the tool down for PM included.
 */
function floorStatus(sources: BriefSources): FloorStatus {
  const out: FloorStatus = { running: [], idle: [], alarm: [], down: [] }
  for (const tool of TOOLS) {
    const shown = displayedReadout(tool, sources.readoutFor(tool))
    out[shown.down ? 'down' : shown.readout.state].push(tool.assetTag)
  }
  return out
}

/** The same walk down the aisle, adding the boards up rather than sorting them. */
function shiftSummary(sources: BriefSources): ShiftSummary {
  let lotsWaiting = 0
  let moves = 0
  let uptimeTotal = 0
  const queues: Array<{ tag: string; bay: string; waiting: number }> = []
  for (const tool of TOOLS) {
    const shown = displayedReadout(tool, sources.readoutFor(tool))
    const state: AndonState = shown.down ? 'down' : shown.readout.state
    const n = andonNumbers(tool, state)
    lotsWaiting += n.wip
    moves += n.moves
    uptimeTotal += n.uptime
    if (n.wip > 0) queues.push({ tag: tool.assetTag, bay: bayNumber(tool), waiting: n.wip })
  }
  queues.sort((a, b) => b.waiting - a.waiting)
  return {
    lotsWaiting,
    moves,
    uptime: Math.round(uptimeTotal / Math.max(TOOLS.length, 1)),
    // The three worst. A supervisor names the queue, not the whole list.
    queues: queues.slice(0, 3),
  }
}

/**
 * The rest of the shift: who they are and where to find them. The speaker
 * is left out - a technician does not need telling where they are standing.
 */
function roster(selfId: string): Array<{ name: string; role: string; where: string }> {
  // A technician whose tool is not on this floor is not on this floor:
  // people.ts never stands them, and a roster that named them would send a
  // student to a bay that is not there.
  const present = (c: CastMember): boolean => !c.toolId || getTool(c.toolId) !== undefined
  return CAST.filter((c) => c.id !== selfId && present(c)).map((c) => {
    // A chase technician with a tool is BEHIND it, not at it: "at ETCH-01
    // in bay 04" would send a student to the front, where Sarah is.
    const at = c.toolId && c.post !== 'chase' ? getTool(c.toolId) : undefined
    return {
      name: castName(c.id),
      role: t(`talk.cast.${c.id}.Role`),
      where: at ? t('talk.floor.AtTool', { tag: at.assetTag, bay: bayNumber(at) }) : t(`talk.cast.${c.id}.Post`),
    }
  })
}

/**
 * The student's round, as the supervisor would have seen it: who they
 * stopped at, what they asked, how it went, and how long ago.
 *
 * The speaker's own conversation is left out - the last few lines of it are
 * already the chat history the model is given, and a man does not need
 * telling what was just said to him. Trimmed to the last few stops, which
 * is as far back as anybody's memory of a shift is any use.
 */
const ROUND_MAX = 6

function round(selfId: string): TalkBrief['floor']['round'] {
  const now = Date.now()
  return talkLog
    .entries()
    .filter((e) => e.who !== selfId)
    .slice(-ROUND_MAX)
    .map((e) => ({
      name: castName(e.who),
      tag: e.tag,
      asked: e.asked,
      outcome: e.outcome,
      ago: Math.max(0, Math.round((now - e.at) / 60_000)),
    }))
}

/**
 * The scene on the screen between them, named the way the menu names it and
 * carrying the catalogue's own "what to look at" line.
 *
 * Both strings come from the same catalogue the scene list is built from, so
 * a technician talking about what is up is quoting the repo rather than
 * remembering a simulation.
 */
/**
 * The report as the brief carries it: the question it answered, and the
 * findings budgeted by the same rule the prediction record uses, so what a
 * technician can quote after a run and what the journal kept of it cannot
 * disagree.
 */
function reported(report: ExperimentReport | null): TalkBrief['reported'] {
  if (!report) return null
  return { question: clip(report.understood, REPORT_QUESTION_MAX), findings: clip(measured(report), REPORTED_MAX) }
}

function onScreen(selfId: string): TalkBrief['onScreen'] {
  const up = talkLog.showing(selfId)
  if (!up) return null
  return {
    id: up.scene,
    title: t(`talk.demo.${up.scene}.Title`),
    watch: tOr(`talk.demo.${up.scene}.Watch`, ''),
    ago: up.ago,
  }
}

/**
 * THE CAPS THIS FILE OWNS. `measured` (predict.ts) always keeps a report's
 * first two findings WHATEVER THEY COST, and a readout's headline and caption
 * are whatever a panel wrote - so neither had a ceiling, and the brief budget
 * `npm run talk` measures was measuring a guess. These are generous (no real
 * readout or report on the floor reaches them today) and exist so the worst
 * case is a number rather than a hope. Cut on a word where there is one.
 */
export const REPORTED_MAX = 900
export const REPORT_QUESTION_MAX = 200
export const SCREEN_HEADLINE_MAX = 80
export const SCREEN_CAPTION_MAX = 160
export const SCREEN_CELL_MAX = 40

function clip(text: string, max: number): string {
  if (text.length <= max) return text
  const cut = text.slice(0, max - 1)
  const space = cut.lastIndexOf(' ')
  return (space > max * 0.6 ? cut.slice(0, space) : cut).trimEnd() + '…'
}

const directory = (): Array<{ tag: string; process: string; bay: string }> =>
  [...TOOLS]
    .sort((a, b) => a.position[0] - b.position[0])
    .map((tool) => ({
      tag: tool.assetTag,
      process: toolProcess(tool),
      bay: bayNumber(tool),
    }))

const LANGUAGE_NAMES: Record<string, string> = {
  en: 'English',
  es: 'Spanish',
  zh_cn: 'Simplified Chinese',
  zh_tw: 'Traditional Chinese',
}

export function buildBrief(who: CastMember, sources: BriefSources): TalkBrief {
  const tool = who.toolId ? getTool(who.toolId) : undefined
  // What the screen beside them ACTUALLY reads, down-for-PM substitution
  // included (hmi.ts) - a technician quoting a screen the student is
  // looking at must quote the same words.
  const shown = tool ? displayedReadout(tool, sources.readoutFor(tool)) : null
  const readout = shown?.readout ?? null
  const spot = tool ? null : sources.whereIs(who.id)
  const down = getTool(MAINTENANCE_TOOL)
  const progress = missionState.progress()
  const armed = sources.houseArmed()
  const fault = armed ? HOUSE_FAULTS.find((f) => f.id === armed) : undefined
  const knowsBuilding = who.post === 'chase' || who.scope === 'floor'
  // The latest decision per scenario: a student who changes their mind has
  // one call on the line, not a history of them, and the supervisor hears
  // the one that stands.
  const latest = new Map<string, string>()
  for (const e of sources.journal.events) {
    if (e.kind !== 'lineDecision') continue
    const scenario = e.scenario ?? 'chiller'
    latest.set(scenario, `${t(`talk.line.Scenario.${scenario}`)}: ${t(`talk.line.Decided.${scenario}.${e.policy}`)}`)
  }
  const decisions = [...latest.values()]
  return {
    person: {
      name: castName(who.id),
      role: t(`talk.cast.${who.id}.Role`),
      post: t(`talk.cast.${who.id}.Post`),
      persona: who.persona,
      // The chase crew are not on the aisle at all: their post line says
      // where they are, and "level with bay 04, heading east" would place
      // them on the wrong side of the wall.
      where: tool || who.post === 'chase' ? '' : whereOnAisle(spot?.x ?? 0, spot?.dir ?? 0),
    },
    tool: tool
      ? { tag: tool.assetTag, name: toolName(tool), process: toolProcess(tool), bay: bayNumber(tool), blurb: toolBlurb(tool) }
      : null,
    screen: readout
      ? {
          state: shown?.down ? 'down for maintenance' : readout.state,
          headline: clip(readout.headline ?? '', SCREEN_HEADLINE_MAX),
          caption: clip(readout.caption ?? '', SCREEN_CAPTION_MAX),
          rows: (readout.rows?.slice(0, 2) ?? []).map(
            ([k, v]) => [clip(String(k), SCREEN_CELL_MAX), clip(String(v), SCREEN_CELL_MAX)] as [string, string],
          ),
        }
      : null,
    workOrder: progress
      ? {
          title: tOr(`mission.${progress.mission.id}.title`, progress.mission.title),
          objectives: progress.mission.objectives.map((o, i) => ({
            text: tOr(`mission.${progress.mission.id}.objective.${o.id}`, o.label),
            met: progress.met[i] ?? false,
          })),
        }
      : null,
    building:
      fault && knowsBuilding
        ? {
            system: tOr(`house.system.${fault.system}.Label`, HOUSE_SYSTEMS[fault.system].label),
            fault: tOr(`house.fault.${fault.id}.Label`, fault.label),
            action: tOr(`house.system.${fault.system}.Action`, HOUSE_SYSTEMS[fault.system].action),
          }
        : null,
    line:
      who.scope === 'floor' && (fault?.system === 'pcw' || decisions.length > 0)
        ? {
            chambersDown: fault?.system === 'pcw' ? t('talk.line.ChambersDown') : null,
            decided: decisions,
          }
        : null,
    floor: {
      down: down ? t('talk.floor.Down', { tag: down.assetTag }) : '',
      layout: t(BALLROOM ? 'talk.floor.LayoutBallroom' : 'talk.floor.Layout'),
      directory: directory(),
      status: who.scope === 'floor' ? floorStatus(sources) : null,
      shift: who.scope === 'floor' ? shiftSummary(sources) : null,
      roster: roster(who.id),
      round: who.scope === 'floor' ? round(who.id) : null,
      worked: who.scope === 'floor' ? workRound(sources.journal, TOOLS, bayNumber) : null,
    },
    // Their own tool, watched from where they are standing. The supervisor
    // was not in the bay and gets the counts instead.
    work: tool ? workAt(sources.journal, tool) : null,
    // Their own memory of their own conversations with this student. The one
    // field here that is not scope-gated and does not need to be: it belongs
    // to the speaker outright.
    taught: talkLog.taughtBy(who.id),
    // What she actually put up, as against the menu below of what she could.
    // Read off the same record that holds what was said (talkLog.ts), so a
    // scene the brief calls live is one she is on record as having shown.
    // What her own tool said, if she has run anything. Her machine, her
    // report, this conversation - the strongest fact she can hold.
    reported: reported(tool ? sources.lastReport() : null),
    onScreen: onScreen(who.id),
    demos: demosFor(who.id).map((d) => ({
      id: d.id,
      title: t(`talk.demo.${d.id}.Title`),
      about: t(`talk.demo.${d.id}.About`),
    })),
    experiments: {
      // A tool with its panel off and a lockout tag on it does not run
      // experiments, and the person holding the tag is the last one who
      // would try. `shown.down` is the same substitution the screen beside
      // him is painted from, so what he can do agrees with what it reads.
      canRun: !!tool && !shown?.down && experimentsFor(who.toolId).length > 0,
      blocked: tool && shown?.down ? t('talk.experiment.LockedOut', { tag: tool.assetTag }) : '',
      menu: tool && !shown?.down ? experimentsFor(who.toolId) : [],
    },
    language: LANGUAGE_NAMES[currentLanguage()] ?? 'English',
  }
}

export const briefFor = (id: string, sources: BriefSources): TalkBrief | null => {
  const who = castById(id)
  return who ? buildBrief(who, sources) : null
}
